/**
 * Mobile device detection utilities
 */

export const isMobileDevice = (): boolean => {
  // Check if window is available (for SSR compatibility)
  if (typeof window === 'undefined') return false;
  
  // Check viewport width
  const isMobileWidth = window.innerWidth <= 768;
  
  // Check user agent for mobile devices
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  const isMobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
    userAgent.toLowerCase()
  );
  
  // Check for touch support
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  
  // Return true if any mobile indicator is present
  return isMobileWidth || isMobileUA || hasTouch;
};

export const isIOS = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  return /iphone|ipad|ipod/i.test(userAgent.toLowerCase());
};

export const isAndroid = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  return /android/i.test(userAgent.toLowerCase());
};

export const isSafari = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  const userAgent = navigator.userAgent || '';
  // Safari has Safari in the user agent, but Chrome on iOS also has Safari
  // We need to check that it's Safari but NOT Chrome
  return /safari/i.test(userAgent) && !/chrome|chromium|crios/i.test(userAgent);
};

export const isChrome = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  const userAgent = navigator.userAgent || '';
  // Chrome on iOS shows as CriOS, Chrome on other platforms shows as Chrome
  return /chrome|chromium|crios/i.test(userAgent);
};

export const isFirefox = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  const userAgent = navigator.userAgent || '';
  // Firefox on iOS shows as FxiOS, Firefox on other platforms shows as Firefox
  return /firefox|fxios/i.test(userAgent);
};

export const getBrowserInfo = () => {
  if (typeof window === 'undefined') {
    return {
      isMobile: false,
      isIOS: false,
      isAndroid: false,
      isSafari: false,
      isChrome: false,
      isFirefox: false,
      browserName: 'unknown',
      hasAuthIssues: false
    };
  }

  const mobile = isMobileDevice();
  const ios = isIOS();
  const android = isAndroid();
  const safari = isSafari();
  const chrome = isChrome();
  const firefox = isFirefox();

  let browserName = 'unknown';
  if (safari) browserName = 'safari';
  else if (chrome) browserName = 'chrome';
  else if (firefox) browserName = 'firefox';

  // Known authentication issues: non-Safari browsers on iOS
  const hasAuthIssues = ios && mobile && !safari;

  return {
    isMobile: mobile,
    isIOS: ios,
    isAndroid: android,
    isSafari: safari,
    isChrome: chrome,
    isFirefox: firefox,
    browserName,
    hasAuthIssues
  };
};